// src/pages/Dashboard/Admin/Inventaire/InventaireDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useInventaireDashboard } from "./hooks/useInventaireDashboard";

import DashboardInventaireHeader from "./components/DashboardInventaireHeader";
import InventaireFiltersCard from "./components/InventaireFiltersCard";
import InventaireKpiCards from "./components/InventaireKpiCards";
import InventaireDataTable from "./components/InventaireDataTable";
import InventaireMobileCards from "./components/InventaireMobileCards";
import InventaireEmptyState from "./components/InventaireEmptyState";
import AddInventaireDrawer from "./components/AddInventaireDrawer";
import EditInventaireDrawer from "./components/EditInventaireDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Inventaire
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function InventaireDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    inventaire,
    filteredInventaire,
    categoryOptions,
    stats,
    filters,
    setFilters,
    loading,
    saving,
    deletingId,
    addDrawerOpen,
    editDrawerOpen,
    currentInventaire,
    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,
    handleCreateInventaire,
    handleUpdateInventaire,
    handleDeleteInventaire,
    handleUploadInventairePhoto,
    refreshInventaire,
  } = useInventaireDashboard();

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardInventaireHeader />

        <InventaireFiltersCard
          filters={filters}
          setFilters={setFilters}
          categoryOptions={categoryOptions}
          loading={loading}
          onAdd={openAddDrawer}
          onRefresh={refreshInventaire}
        />

        <InventaireKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste inventaire
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Matériel, stock et catégories
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Consultez rapidement les objets du club, leur stock, leur
                  catégorie et les notes associées.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Objets affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredInventaire.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {inventaire.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredInventaire.length === 0 ? (
              <InventaireEmptyState onAdd={openAddDrawer} />
            ) : (
              <div className="mt-5">
                <InventaireDataTable
                  items={filteredInventaire}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteInventaire}
                  deletingId={deletingId}
                />

                <InventaireMobileCards
                  items={filteredInventaire}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteInventaire}
                  deletingId={deletingId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AddInventaireDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        onSubmit={handleCreateInventaire}
        saving={saving}
        categoryOptions={categoryOptions}
      />

      <EditInventaireDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        inventaire={currentInventaire}
        onSubmit={handleUpdateInventaire}
        onDelete={handleDeleteInventaire}
        onUploadPhoto={handleUploadInventairePhoto}
        saving={saving}
        deletingId={deletingId}
      />

      <ToastContainer />
    </section>
  );
}
